/**
 * Client-side mirror of the runtime's ingestion safeguards gate
 * (`ingest-safeguards.ts`; handoff `ingest-safeguards-lexide-handoff-2026-07-20.md`).
 *
 * THE SERVER STAYS AUTHORITATIVE. Everything here is advisory and trivially
 * bypassable — its only job is to fail FAST with a good message instead of
 * base64-ing 40 MB over the wire to earn a rejection.
 *
 * Order mirrors the server's deliberately (denylist → transport → per-type),
 * because the order is observable: it decides WHICH limit the user is told
 * about, and telling them the wrong one is worse than saying nothing.
 *
 * Pure + IO-free so the rules are unit-testable without a gateway.
 *
 * ⚖ **THE REFUSAL POLICY ITSELF NOW LIVES IN `upload-policy.mjs`** and is
 * re-exported here unchanged, so every existing import of this module keeps
 * working. It moved because an out-of-process driver has to consume it — see
 * that file's header for the full reason. What stays here is what genuinely
 * needs TypeScript or is SPA-only: the picker's `accept` derivation.
 *
 * ⚠ Do NOT re-implement any re-exported symbol here. One copy, in the leaf.
 */
import type { DocumentUploadLimits } from "@/types/gateway";
export {
  extensionOf,
  effectiveMaxBytes,
  preValidateUpload,
  selectUploadTransport,
} from "./upload-policy.mjs";
export type {
  UploadRefusalCode,
  UploadRefusal,
  UploadTransportDecision,
} from "./upload-policy.mjs";
import { extensionOf } from "./upload-policy.mjs";

/**
 * Extensions the picker should accept.
 *
 * PRIMARY source: the server's `acceptedExtensions` — a first-class field on
 * `document.upload_limits`, derived from the runtime's ROUTE TABLE. The cap
 * table is NOT a substitute: a type with no explicit cap is a perfectly normal
 * accepted type (it rides on `defaultMaxBytes`) but has no `fileSizeBytes`
 * key, so deriving the picker from the caps silently hid the whole
 * default-cap family (.txt/.md/.odt/.rtf, .tif/.heif, .aac/.flac…) while the
 * server accepted them all.
 *
 * FALLBACK (field absent ⇒ older runtime; the two deploy independently): the
 * historical cap-table derivation — exactly that runtime's era of behaviour,
 * so the transition window regresses nothing.
 *
 * The denylist filter and the `.zip`/archive gate are re-applied to the
 * server list as belt-and-braces; the server already applies both.
 */
export function acceptedExtensions(limits: DocumentUploadLimits): string[] {
  const denied = new Set((limits.deniedExtensions ?? []).map((e) => e.toLowerCase()));
  const source = limits.acceptedExtensions ?? Object.keys(limits.fileSizeBytes ?? {});
  return source
    .map((e) => e.toLowerCase())
    .filter((e) => !denied.has(e))
    .filter((e) => e !== ".zip" || limits.archive?.enabled !== false)
    .sort();
}

/** `accept` attribute value for `<input type="file">`. */
export function acceptAttribute(limits: DocumentUploadLimits | undefined): string | undefined {
  if (!limits) return undefined;
  const exts = acceptedExtensions(limits);
  return exts.length > 0 ? exts.join(",") : undefined;
}
